import "../../kendo.icons.js";
import "../../kendo.html.button.js";
import { STYLES, REFERENCES, ICONS } from "../constants";
import type { IPromptBoxMessages, IActionButtonSettings, IFileSelectButtonSettings, ISpeechToTextButtonSettings } from "../models";
import type { ModeValue } from "../constants";
import { LINE_MODE } from "../constants";

declare const kendo: any;

/**
 * Renders the PromptBox header section.
 * Returns empty string when no headerTemplate (header is created dynamically when needed).
 */
export const renderHeader = (
    headerTemplate: (() => string) | null,
    forceRender: boolean = false
): string => {
    let content = "";
    if (headerTemplate) {
        content = headerTemplate();
    }

    if (!content && !forceRender) {
        return "";
    }

    const hiddenClass = content ? "" : ` ${STYLES.hidden}`;
    return `<div class="${STYLES.promptBoxHeader}${hiddenClass}" ${REFERENCES.header}><div ${REFERENCES.attachmentsHost}></div>${content}</div>`;
};

/**
 * Renders a template function.
 */
const renderTemplate = (template: (() => string) | null | undefined): string => {
    if (!template) {
        return "";
    }
    return template();
};

/**
 * Renders the send button using kendo.html.renderButton.
 */
export const renderSendButton = (
    messages: IPromptBoxMessages,
    enable: boolean = true,
    settings?: IActionButtonSettings
): string => {
    const disabledClass = !enable ? ` ${STYLES.disabled}` : "";
    const ariaDisabled = !enable ? ' aria-disabled="true"' : '';

    const buttonOptions: Record<string, any> = {
        icon: settings?.icon || "arrow-up-outline",
        size: settings?.size || "small",
        rounded: settings?.rounded || "full"
    };

    if (settings?.fillMode) {
        buttonOptions.fillMode = settings.fillMode;
    }
    if (settings?.themeColor) {
        buttonOptions.themeColor = settings.themeColor;
    }

    return kendo.html.renderButton(
        `<button title="${messages.actionButton}" aria-label="${messages.actionButton}" aria-live="polite" class="${disabledClass}"${ariaDisabled} ${REFERENCES.sendButton} type="button"></button>`,
        buttonOptions
    );
};

/**
 * Renders the file select button and hidden file input using kendo.html.renderButton.
 */
export const renderFileSelectButton = (
    fileInputId: string,
    accept?: string,
    multiple?: boolean,
    settings?: IFileSelectButtonSettings,
    enable: boolean = true,
    messages?: IPromptBoxMessages
): string => {
    const multipleAttr = multiple ? ' multiple' : '';
    const acceptAttr = accept ? ` accept="${accept}"` : '';
    const disabledAttr = !enable ? ' disabled' : '';
    const ariaDisabledAttr = !enable ? ' aria-disabled="true"' : '';
    const disabledClass = !enable ? ` ${STYLES.disabled}` : '';
    const buttonTitle = settings?.text || messages?.fileSelectButton || "Attach file";

    const buttonOptions: Record<string, any> = {
        icon: settings?.icon || ICONS.attachment,
        fillMode: settings?.fillMode || "clear",
        size: settings?.size || "small",
        rounded: settings?.rounded || "full"
    };

    if (settings?.themeColor) {
        buttonOptions.themeColor = settings.themeColor;
    }

    const button = kendo.html.renderButton(
        `<button title="${buttonTitle}" aria-label="${buttonTitle}" class="${disabledClass}" ${REFERENCES.fileSelectButton} type="button"${disabledAttr}${ariaDisabledAttr}></button>`,
        buttonOptions
    );

    return `${button}<input type="file" id="${fileInputId}" class="${STYLES.hidden}" ${REFERENCES.fileInput}${multipleAttr}${acceptAttr} />`;
};

/**
 * Renders the speech-to-text button placeholder.
 * The actual icon is rendered by the SpeechToTextButton component.
 */
export const renderSpeechToTextButton = (settings?: ISpeechToTextButtonSettings): string => {
    return `<button class="${STYLES.speechToTextButton}" type="button"></button>`;
};

/**
 * Affix configuration for rendering.
 */
export interface IAffixConfig {
    startAffixTemplate?: (() => string) | null;
    endAffixTemplate?: (() => string) | null;
    topAffixTemplate?: (() => string) | null;
}

/**
 * File select button configuration for rendering.
 */
export interface IFileSelectButtonConfig {
    enable?: boolean;
    fileInputId: string;
    accept?: string;
    multiple?: boolean;
    showSpacer?: boolean;
    settings?: IFileSelectButtonSettings;
}

/**
 * Button settings configuration for rendering.
 */
export interface IButtonSettingsConfig {
    actionButtonSettings?: IActionButtonSettings;
    fileSelectButtonSettings?: IFileSelectButtonSettings;
    speechToTextButtonSettings?: ISpeechToTextButtonSettings;
}

/**
 * Combined rendering configuration.
 */
export interface IRenderConfig {
    affixConfig?: IAffixConfig;
    fileSelectButtonConfig?: IFileSelectButtonConfig;
    buttonSettingsConfig?: IButtonSettingsConfig;
}

/**
 * Renders the start affix container (before the input).
 * Uses k-prompt-box-affix class; position is determined by DOM order.
 */
export const renderStartAffix = (
    startAffixTemplate: (() => string) | null | undefined
): string => {
    const content = renderTemplate(startAffixTemplate);
    if (!content) {
        return "";
    }
    return `<div class="${STYLES.promptBoxAffix}" ${REFERENCES.startAffix}>${content}</div>`;
};

/**
 * Renders the end affix container with buttons (after the input).
 * Returns empty string when no content.
 */
export const renderEndAffix = (
    messages: IPromptBoxMessages,
    showSendButton: boolean,
    showSpeechToText: boolean = true,
    endAffixTemplate?: (() => string) | null,
    fileSelectButtonConfig?: IFileSelectButtonConfig,
    buttonSettingsConfig?: IButtonSettingsConfig
): string => {
    const endAffix = renderTemplate(endAffixTemplate);
    const fileSelectButtonEnabled = fileSelectButtonConfig?.enable !== false;
    const fileSelectButton = fileSelectButtonConfig
        ? renderFileSelectButton(fileSelectButtonConfig.fileInputId, fileSelectButtonConfig.accept, fileSelectButtonConfig.multiple, fileSelectButtonConfig.settings, fileSelectButtonEnabled, messages)
        : "";
    const spacer = fileSelectButtonConfig?.showSpacer ? `<div class="${STYLES.spacer}"></div>` : "";
    const speechButton = showSpeechToText ? renderSpeechToTextButton(buttonSettingsConfig?.speechToTextButtonSettings) : "";
    const sendButton = showSendButton ? renderSendButton(messages, true, buttonSettingsConfig?.actionButtonSettings) : "";

    const content = `${endAffix}${fileSelectButton}${spacer}${speechButton}${sendButton}`.trim();
    if (!content) {
        return "";
    }

    return `<div class="${STYLES.promptBoxAffix}" ${REFERENCES.endAffix}>
        ${endAffix}
        ${fileSelectButton}
        ${spacer}
        ${speechButton}
        ${sendButton}
    </div>`;
};

/**
 * Renders the end affix container with start affix content (for multi mode).
 * In multi mode: fileSelectButton + startAffix content + spacer + endAffix content + buttons
 * The spacer is only rendered if there's content on both sides to separate.
 */
export const renderEndAffixWithStartAffix = (
    messages: IPromptBoxMessages,
    showSendButton: boolean,
    showSpeechToText: boolean = true,
    startAffixTemplate?: (() => string) | null,
    endAffixTemplate?: (() => string) | null,
    fileSelectButtonConfig?: IFileSelectButtonConfig,
    buttonSettingsConfig?: IButtonSettingsConfig
): string => {
    const startAffix = renderTemplate(startAffixTemplate);
    const endAffix = renderTemplate(endAffixTemplate);
    const fileSelectButtonEnabled = fileSelectButtonConfig?.enable !== false;
    const fileSelectButton = fileSelectButtonConfig
        ? renderFileSelectButton(fileSelectButtonConfig.fileInputId, fileSelectButtonConfig.accept, fileSelectButtonConfig.multiple, fileSelectButtonConfig.settings, fileSelectButtonEnabled, messages)
        : "";
    const speechButton = showSpeechToText ? renderSpeechToTextButton(buttonSettingsConfig?.speechToTextButtonSettings) : "";
    const sendButton = showSendButton ? renderSendButton(messages, true, buttonSettingsConfig?.actionButtonSettings) : "";

    const spacer = `<div class="${STYLES.spacer}"></div>`;

    if (!fileSelectButtonConfig?.showSpacer) {
         return `<div class="${STYLES.promptBoxAffix}" ${REFERENCES.endAffix}>
            ${startAffix}
            ${spacer}
            ${endAffix}
            ${fileSelectButton}
            ${speechButton}
            ${sendButton}
        </div>`;
    }

    return `<div class="${STYLES.promptBoxAffix}" ${REFERENCES.endAffix}>
        ${fileSelectButton}
        ${startAffix}
        ${spacer}
        ${endAffix}
        ${speechButton}
        ${sendButton}
    </div>`;
};

/**
 * Renders the top affix section (only for multi mode).
 * Note: This is placed at the beginning of content for multi-mode layout.
 */
export const renderTopAffix = (
    topAffixTemplate: (() => string) | null | undefined
): string => {
    const content = renderTemplate(topAffixTemplate);
    if (!content) {
        return "";
    }
    return `<div class="${STYLES.promptBoxAffix}" ${REFERENCES.topAffix}>${content}</div>`;
};

/**
 * Renders the complete PromptBox structure.
 * For multi mode: startAffix content is rendered inside the end affix container with a spacer.
 * For auto mode: startAffix is rendered as a separate container (JS handles reorganization on expand).
 */
export const renderPromptBox = (
    headerTemplate: (() => string) | null,
    messages: IPromptBoxMessages,
    showSendButton: boolean,
    mode: ModeValue,
    showSpeechToText: boolean = true,
    renderConfig: IRenderConfig = {}
): string => {
    const { affixConfig = {}, fileSelectButtonConfig, buttonSettingsConfig } = renderConfig;
    const forceRenderHeader = !!fileSelectButtonConfig;
    const header = renderHeader(headerTemplate, forceRenderHeader);
    const topAffix = mode === LINE_MODE.MULTI ? renderTopAffix(affixConfig.topAffixTemplate) : "";

    const isMultiMode = mode === LINE_MODE.MULTI;

    const startAffix = isMultiMode ? "" : renderStartAffix(affixConfig.startAffixTemplate);
    const endAffix = isMultiMode
        ? renderEndAffixWithStartAffix(messages, showSendButton, showSpeechToText, affixConfig.startAffixTemplate, affixConfig.endAffixTemplate, fileSelectButtonConfig, buttonSettingsConfig)
        : renderEndAffix(messages, showSendButton, showSpeechToText, affixConfig.endAffixTemplate, fileSelectButtonConfig, buttonSettingsConfig);

    return `${header}
        <div class="${STYLES.promptBoxContent}">
            ${topAffix}
            ${startAffix}
            <span ${REFERENCES.input}></span>
            ${endAffix}
        </div>`;
};
